import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { returnService, productService } from '../services/api';
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp } from '../components/common';

const getStatusVariant = (status) => {
  return 'success'; // Only RECEIVED - from backend
};

// Status is read-only from backend; no status change allowed
function ReturnStatusCell({ status, getStatusVariant }) {
  if (!status) return '—';
  return (
    <Badge variant={getStatusVariant(status)}>
      <span>{status.replace(/_/g, ' ')}</span>
    </Badge>
  );
}

export function ReturnManagement() {
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [outwardInvoices, setOutwardInvoices] = useState([]);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    productId: '',
    invoiceQuantity: 0,
    receivedQuantity: 0,
    returnReason: 'DEFECTIVE',
    vehicleNumber: '',
    location: '',
    batches: [],
  });
  const [currentBatch, setCurrentBatch] = useState({
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    quantity: 0,
  });
  const [loading, setLoading] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [editingTransactionId, setEditingTransactionId] = useState(null);

  const returnColumns = [
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'invoiceNumber', label: 'Invoice' },
    {
      key: 'productId',
      label: 'Product',
      render: (val) => (val?.sku ?? '—'),
    },
    { key: 'receivedQuantity', label: 'Received Qty', align: 'right' },
    { key: 'returnReason', label: 'Return Reason' },
    {
      key: 'vehicleNumber',
      label: 'Vehicle',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <ReturnStatusCell status={val} getStatusVariant={getStatusVariant} />
      ),
    },
  ];

  useEffect(() => {
    fetchProducts();
    fetchTransactions();
    fetchOutwardInvoices();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Also watch for outwardInvoices loading - if invoiceNumber is already set, update vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0 && showForm) {
      const invoice = outwardInvoices.find(
        (inv) => inv.invoiceNumber === formData.invoiceNumber
      );
      
      if (invoice && invoice.vehicleNumber) {
        // Update vehicle number if it's different or not set
        setFormData((prev) => {
          if (prev.vehicleNumber !== invoice.vehicleNumber) {
            return {
              ...prev,
              vehicleNumber: invoice.vehicleNumber,
            };
          }
          return prev;
        });
      }
    }
  }, [outwardInvoices.length, formData.invoiceNumber, showForm]);

  // Watch for invoice number changes and auto-populate invoice date and vehicle number
  useEffect(() => {
    if (formData.invoiceNumber && outwardInvoices.length > 0) {
      const invoice = outwardInvoices.find(
        (inv) => inv.invoiceNumber === formData.invoiceNumber
      );
      
      if (invoice) {
        // Set selected invoice data
        setSelectedInvoiceData(invoice);
        // Reset selected products when invoice changes
        setSelectedProducts([]);
        
        // Prepare update data
        const updateData = {};
        
        // Parse and set invoice date
        if (invoice.invoiceDate) {
          try {
            let dateObj;
            const invoiceDateValue = invoice.invoiceDate;
            
            // Handle different date formats
            if (typeof invoiceDateValue === 'string') {
              dateObj = new Date(invoiceDateValue);
            } else if (invoiceDateValue instanceof Date) {
              dateObj = invoiceDateValue;
            } else if (invoiceDateValue && typeof invoiceDateValue === 'object' && invoiceDateValue.$date) {
              // MongoDB date format
              dateObj = new Date(invoiceDateValue.$date);
            } else {
              dateObj = new Date(invoiceDateValue);
            }
            
            if (!isNaN(dateObj.getTime())) {
              const formattedDate = dateObj.toISOString().split('T')[0];
              updateData.invoiceDate = formattedDate;
            } else {
              console.warn('Invalid date format:', invoiceDateValue);
            }
          } catch (error) {
            console.error('Error parsing invoice date:', error, invoice.invoiceDate);
          }
        }
        
        // Always set vehicle number (don't depend on date parsing)
        updateData.vehicleNumber = invoice.vehicleNumber || '';
        
        // Update form data - always update vehicle number when invoice changes
        setFormData((prev) => {
          const newData = { ...prev };
          let hasChanges = false;
          
          // Update invoice date if different
          if (updateData.invoiceDate && prev.invoiceDate !== updateData.invoiceDate) {
            newData.invoiceDate = updateData.invoiceDate;
            hasChanges = true;
          }
          
          // Always update vehicle number when invoice changes (even if same value, to ensure it's set)
          if (prev.vehicleNumber !== updateData.vehicleNumber) {
            newData.vehicleNumber = updateData.vehicleNumber;
            hasChanges = true;
          }
          
          return hasChanges ? newData : prev;
        });
      }
    }
  }, [formData.invoiceNumber, outwardInvoices]);

  const fetchProducts = async () => {
    try {
      const result = await productService.getProducts();
      setProducts(result.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const result = await returnService.getReturnTransactions(params);
      setTransactions(result.data || []);
    } catch (error) {
      console.error('Error fetching return transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutwardInvoices = async () => {
    try {
      const result = await returnService.getOutwardInvoicesForReturn();
      setOutwardInvoices(result.data || []);
    } catch (error) {
      console.error('Error fetching outward invoices:', error);
      toast.error('Failed to load outward invoices');
    }
  };

  // Get unique invoice numbers from outward invoices
  const getUniqueInvoices = () => {
    return outwardInvoices.map((inv) => ({
      invoiceNumber: inv.invoiceNumber,
      invoiceDate: inv.invoiceDate,
      vehicleNumber: inv.vehicleNumber,
      outwardTransactionId: inv.outwardTransactionId,
    }));
  };

  const resetForm = () => {
    setFormData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      productId: '',
      invoiceQuantity: 0,
      receivedQuantity: 0,
      returnReason: 'DEFECTIVE',
      vehicleNumber: '',
      location: '',
      batches: [],
    });
    setSelectedProducts([]);
    setSelectedInvoiceData(null);
    setFormErrors({});
    setTouched({});
    setEditingTransactionId(null);
    setCurrentBatch({
      batchNumber: '',
      manufacturingDate: '',
      expiryDate: '',
      quantity: 0,
    });
  };

  // Wrapper for setFormData that includes validation
  const handleSetFormData = (updater) => {
    setFormData((prevFormData) => {
      const newFormData = typeof updater === 'function' ? updater(prevFormData) : updater;
      
      // Validate received quantity whenever it changes
      if (newFormData.receivedQuantity !== prevFormData.receivedQuantity) {
        setTouched((prev) => ({ ...prev, receivedQuantity: true }));
        
        const receivedQty = parseFloat(newFormData.receivedQuantity) || 0;
        const invoiceQty = newFormData.invoiceQuantity || 0;
        
        if (receivedQty < 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: 'Received quantity cannot be negative',
          }));
        } else if (receivedQty > invoiceQty && invoiceQty > 0) {
          setFormErrors((prev) => ({
            ...prev,
            receivedQuantity: `Received quantity cannot exceed invoice quantity (${invoiceQty})`,
          }));
        } else {
          // Clear error if valid
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.receivedQuantity;
            return newErrors;
          });
        }
        
        // Validate batches sum when receivedQuantity changes
        if (receivedQty > 0 && newFormData.batches && newFormData.batches.length > 0) {
          const batchesSum = newFormData.batches.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0);
          if (batchesSum !== receivedQty) {
            setFormErrors((prev) => ({
              ...prev,
              batches: `Sum of batch quantities (${batchesSum}) must equal received quantity (${receivedQty})`,
            }));
          } else {
            setFormErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors.batches;
              return newErrors;
            });
          }
        }
      }
      
      return newFormData;
    });
  };

  const handleProductToggle = (productId, dispatchQuantity, batchDetails = []) => {
    setSelectedProducts((prev) => {
      const productIdStr = String(productId);
      const isSelected = prev.some((p) => String(p.productId) === productIdStr);
      
      if (isSelected) {
        // Remove product from selection - clear everything
        setFormData((prevForm) => ({
          ...prevForm,
          productId: '',
          invoiceQuantity: 0,
          batches: [],
          receivedQuantity: 0,
        }));
        return [];
      } else {
        // Single select: replace previous selection with new one
        // Get batch details for the selected product
        const productItem = getInvoiceProducts().find(
          (item) => String(item.productId) === productIdStr
        );
        const batches = productItem?.batchDetails || batchDetails || [];
        
        const newInvoiceQty = dispatchQuantity;
        setFormData((prevForm) => {
          const updated = {
            ...prevForm,
            productId: productId,
            invoiceQuantity: newInvoiceQty,
            batches: batches.map((batch) => ({
              batchNumber: batch.batchNumber || '',
              manufacturingDate: batch.manufacturingDate 
                ? new Date(batch.manufacturingDate).toISOString().split('T')[0] 
                : '',
              expiryDate: batch.expiryDate 
                ? new Date(batch.expiryDate).toISOString().split('T')[0] 
                : '',
              quantity: 0, // Start with 0, user will enter return quantity
              // Store original quantity (outward dispatch quantity) and available quantity
              originalQuantity: batch.originalQuantity || batch.quantity || 0,
              availableQuantity: batch.availableQuantity !== undefined ? batch.availableQuantity : (batch.quantity || 0),
              alreadyReturned: batch.alreadyReturned || 0,
            })),
            // Reset receivedQuantity when product changes
            receivedQuantity: 0,
          };
          
          // Clear any existing errors
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.receivedQuantity;
            return newErrors;
          });
          
          return updated;
        });
        // Return single item array (single select)
        return [{ productId, dispatchQuantity, batchDetails }];
      }
    });
  };

  const handleBatchChange = (e) => {
    const { name, value } = e.target;
    setCurrentBatch({
      ...currentBatch,
      [name]: name === 'quantity' ? parseFloat(value) : value,
    });
  };

  const addBatch = () => {
    if (currentBatch.batchNumber && currentBatch.quantity > 0) {
      setFormData({
        ...formData,
        batches: [...formData.batches, { ...currentBatch }],
      });
      setCurrentBatch({
        batchNumber: '',
        manufacturingDate: '',
        expiryDate: '',
        quantity: 0,
      });
    }
  };

  const removeBatch = (index) => {
    setFormData({
      ...formData,
      batches: formData.batches.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (selectedProducts.length === 0) {
      toast.error('Please select a product');
      return;
    }
    
    // Single product selection enforced
    if (selectedProducts.length > 1) {
      toast.error('Please select only one product');
      return;
    }
    
    const firstProduct = selectedProducts[0];
    const invoice = outwardInvoices.find(
      (inv) => inv.invoiceNumber === formData.invoiceNumber
    );
    
    if (!invoice || !invoice.outwardTransactionId) {
      toast.error('Invalid invoice selection. Please select a valid invoice.');
      return;
    }
    
    // Validate received quantity
    const receivedQty = parseFloat(formData.receivedQuantity) || 0;
    const invoiceQty = formData.invoiceQuantity || 0;
    
    if (receivedQty < 0) {
      toast.error('Received quantity cannot be negative');
      return;
    }
    
    if (receivedQty > invoiceQty) {
      toast.error(`Received quantity (${receivedQty}) cannot exceed invoice quantity (${invoiceQty})`);
      return;
    }
    
    // Validate batches when receivedQuantity > 0
    if (receivedQty > 0) {
      if (!formData.batches || formData.batches.length === 0) {
        toast.error('Batches are required when received quantity > 0');
        return;
      }
      
      // Validate batch quantities sum equals receivedQuantity
      const batchesSum = formData.batches.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0);
      if (batchesSum !== receivedQty) {
        toast.error(`Sum of batch quantities (${batchesSum}) must equal received quantity (${receivedQty})`);
        return;
      }
      
      // Validate each batch has required fields
      for (const batch of formData.batches) {
        const batchNum = (batch.batchNumber || '').toString().trim();
        const batchQty = parseFloat(batch.quantity) || 0;
        if (!batchNum) {
          toast.error('Batch number is required for all batches');
          return;
        }
        if (batchQty < 1) {
          toast.error('Batch quantity must be >= 1 when received quantity > 0');
          return;
        }
      }
    } else {
      // If receivedQuantity === 0, ensure batches are empty or all quantities are 0
      if (formData.batches && formData.batches.length > 0) {
        const hasNonZeroQuantity = formData.batches.some(batch => (parseFloat(batch.quantity) || 0) > 0);
        if (hasNonZeroQuantity) {
          toast.error('When received quantity is 0, batch quantities must be 0 or batches must be empty');
          return;
        }
      }
    }
    
    // Build dataToSubmit - ensure correct structure
    const dataToSubmit = {
      invoiceNumber: formData.invoiceNumber,
      invoiceDate: formData.invoiceDate,
      productId: firstProduct.productId,
      invoiceQuantity: firstProduct.dispatchQuantity,
      receivedQuantity: receivedQty,
      returnReason: formData.returnReason,
      vehicleNumber: formData.vehicleNumber || '',
      location: formData.location?.trim() || undefined,
      outwardTransactionId: invoice.outwardTransactionId,
      // If receivedQuantity === 0, send empty batches array
      batches: receivedQty > 0 ? formData.batches : [],
    };
    
    try {
      if (editingTransactionId) {
        // Update existing transaction
        await returnService.updateReturnTransaction(editingTransactionId, dataToSubmit);
        toast.success('Return transaction updated successfully');
      } else {
        // Create new transaction
        await returnService.createReturnTransaction(dataToSubmit);
        toast.success('Return transaction created successfully');
      }
      
      resetForm();
      setShowForm(false);
      fetchTransactions();
      window.dispatchEvent(new CustomEvent('putaway-refresh'));
    } catch (error) {
      console.error('Error saving return transaction:', error);
      toast.error(error?.message || 'Failed to save return transaction');
    }
  };

  const handleEdit = async (row) => {
    try {
      // Fetch full transaction details including batches
      const transactionDetails = await returnService.getReturnTransactionById(row._id || row.id);
      const transaction = transactionDetails.data || transactionDetails;
      
      // Format batches for form
      const formattedBatches = (transaction.batches || []).map((batch) => ({
        batchNumber: batch.batchNumber || '',
        manufacturingDate: batch.manufacturingDate 
          ? new Date(batch.manufacturingDate).toISOString().split('T')[0]
          : '',
        expiryDate: batch.expiryDate 
          ? new Date(batch.expiryDate).toISOString().split('T')[0]
          : '',
        quantity: batch.quantity || 0,
        originalQuantity: batch.quantity || 0, // For edit, use current quantity as original
        availableQuantity: batch.quantity || 0,
        alreadyReturned: 0,
      }));
      
      // Find the invoice from outward invoices to get product details
      const invoice = outwardInvoices.find(
        (inv) => inv.invoiceNumber === transaction.invoiceNumber
      );
      
      // Find product in invoice items
      let productItem = null;
      if (invoice && invoice.items) {
        productItem = invoice.items.find(
          (item) => String(item.productId) === String(transaction.productId)
        );
      }
      
      // Set selected product
      if (productItem) {
        setSelectedProducts([{
          productId: transaction.productId,
          dispatchQuantity: transaction.invoiceQuantity,
          batchDetails: productItem.batchDetails || [],
        }]);
      }
      
      // Populate form with existing transaction data
      setFormData({
        invoiceNumber: transaction.invoiceNumber || '',
        invoiceDate: transaction.invoiceDate 
          ? new Date(transaction.invoiceDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        productId: transaction.productId?._id || transaction.productId || '',
        invoiceQuantity: transaction.invoiceQuantity || 0,
        receivedQuantity: transaction.receivedQuantity || 0,
        returnReason: transaction.returnReason || 'DEFECTIVE',
        vehicleNumber: transaction.vehicleNumber || '',
        location: transaction.location || '',
        batches: formattedBatches,
      });
      
      // Set selected invoice data
      if (invoice) {
        setSelectedInvoiceData(invoice);
      }
      
      setEditingTransactionId(transaction._id || row._id || row.id);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to load transaction details');
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await returnService.deleteReturnTransaction(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchTransactions();
      toast.success('Return transaction deleted successfully');
    } catch (err) {
      console.error('Error deleting return transaction:', err);
      toast.error(err?.message || 'Failed to delete return transaction');
    } finally {
      setDeleteLoading(false);
    }
  };

  const returnActions = [
    {
      label: 'Edit',
      icon: EditIcon,
      onClick: handleEdit,
    },
    {
      label: 'Delete',
      icon: DeleteIcon,
      onClick: handleDelete,
    },
  ];

  // Get products from selected invoice
  const getInvoiceProducts = () => {
    if (!selectedInvoiceData) return [];
    
    // Find the invoice from outwardInvoices
    const invoice = outwardInvoices.find(
      (inv) => inv.invoiceNumber === selectedInvoiceData.invoiceNumber
    );
    
    if (!invoice || !invoice.items || invoice.items.length === 0) {
      console.log('No items found for invoice:', selectedInvoiceData.invoiceNumber);
      return [];
    }
    
    console.log('Invoice items:', invoice.items);
    
    // Return items with product details - ensure all products are included
    const productsList = invoice.items.map((item) => {
      // Handle product data - it might be populated object or just ID
      let product = item.product;
      const productId = item.productId;
      
      // If product is not populated, find it from products list
      if (!product || typeof product === 'string') {
        product = products.find((p) => p._id === productId || String(p._id) === String(productId));
      }
      
      return {
        productId: productId,
        product: product,
        dispatchQuantity: item.dispatchQuantity || 0,
        batchDetails: item.batchDetails || [],
      };
    }).filter((item) => item.productId); // Filter out any items without productId
    
    console.log('Processed products list:', productsList);
    return productsList;
  };

  const getReturnFields = () => [
    {
      name: 'invoiceNumber',
      label: 'Invoice Number',
      type: 'select',
      required: true,
      placeholder: 'Enter no',
      options: getUniqueInvoices().map((inv) => ({
        label: inv.invoiceNumber,
        value: inv.invoiceNumber,
      })),
      grid: '1',
    },
    {
      name: 'invoiceDate',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      grid: '1',
      disabled: true,
    },
    {
      name: 'invoiceQuantity',
      label: 'Invoice Quantity',
      type: 'number',
      required: true,
      min: 1,
      grid: '1',
      disabled: true,
    },
    {
      name: 'returnReason',
      label: 'Return Reason',
      type: 'select',
      required: true,
      options: [
        { label: 'Defective', value: 'DEFECTIVE' },
        { label: 'Expired', value: 'EXPIRED' },
        { label: 'Quality Issue', value: 'QUALITY_ISSUE' },
        { label: 'Customer Return', value: 'CUSTOMER_RETURN' },
        { label: 'Other', value: 'OTHER' },
      ],
      grid: '1',
    },
    {
      name: 'receivedQuantity',
      label: 'Received Quantity',
      type: 'number',
      required: false,
      min: 0,
      max: formData.invoiceQuantity || 999999,
      grid: '1',
    },
    {
      name: 'vehicleNumber',
      label: 'Vehicle Number',
      type: 'text',
      grid: '1',
      disabled: true,
    },
    {
      name: 'location',
      label: 'Location',
      type: 'text',
      grid: '1',
      placeholder: 'e.g. Warehouse A',
    },
  ];
  const handleExportReturnGRN = () => {
   // ✅ UI only for now (later backend connect)
   toast.info("GRN export will be added soon.");
 };
  return (
    <div className="page-container">
    <div className="mb-6">
  <div className="rounded-[28px] border border-gray-100 bg-white px-6 py-4 shadow-sm">
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      
      {/* Left */}
      <div className="min-w-0">
        <h1 className="whitespace-nowrap text-lg font-medium text-gray-900">
          Return Management
        </h1>
      </div>

      {/* Right */}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
        
        {/* ✅ EXACT same search as Company */}
        <div className="w-full sm:w-[320px]">
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID"
              className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
          </div>
        </div>

        {/* ✅ EXACT same button style */}
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="h-11 shrink-0 rounded-xl bg-emerald-700 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 active:scale-[0.98]"
        >
          Create Return Transaction
        </button>
      </div>
    </div>
  </div>
</div>



      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        entityLabel="return transaction"
      />
      <FormPopup
        open={showForm}
        title={editingTransactionId ? "Edit Return Transaction" : "Create New Return"}
        submitText={editingTransactionId ? "Update" : "Save"}
        onClose={() => {
          resetForm();
          setShowForm(false);
        }}
        onSubmit={handleSubmit}
        values={formData}
        setValues={handleSetFormData}
        fields={getReturnFields()}
        errors={formErrors}
        touched={touched}
        width="xl"
        bodyGridColumns={4}
      >
        {selectedInvoiceData && (
          <div className="mt-6 rounded-3xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Products</h3>
            <div className="space-y-3">
              {getInvoiceProducts().map((item, index) => {
                const isSelected = selectedProducts.some((p) => String(p.productId) === String(item.productId));
                const product = item.product;
                const productKey = item.productId || `product-${index}`;
                
                return (
                  <label
                    key={productKey}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="productSelection"
                      checked={isSelected}
                      onChange={() => handleProductToggle(item.productId, item.dispatchQuantity, item.batchDetails)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {product?.sku || item.productId || 'N/A'} - {product?.description || 'Product not found'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Dispatch Quantity: <span className="font-medium">{item.dispatchQuantity}</span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="text-sm font-medium text-emerald-600">
                        Invoice Qty: {item.dispatchQuantity}
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
            {getInvoiceProducts().length === 0 && selectedInvoiceData && (
              <div className="text-sm text-gray-500 text-center py-4">
                <p>No products found for invoice: {selectedInvoiceData.invoiceNumber}</p>
                <p className="text-xs mt-2">Please check if the invoice has items in outward management.</p>
              </div>
            )}
          </div>
        )}
        <div
          style={{
            border: '1px solid #E4E7EC',
            borderRadius: 14,
            background: '#FFFFFF',
            padding: 18,
            marginTop: 18,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#101828', margin: '0 0 16px 0' }}>Batch Details</h3>
          {formData.batches.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Please select a product to view batch details
            </p>
          ) : (
            <>
            {formErrors.batches && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{formErrors.batches}</p>
              </div>
            )}
            <div className="mb-4 text-sm text-gray-600">
              <p>Adjust batch quantities below. Total must equal <strong>Received Quantity: {formData.receivedQuantity || 0}</strong></p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Batch Number</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Mfg Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Expr Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Quanity</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.batches.map((batch, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-sm text-gray-700">{batch.batchNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {batch.manufacturingDate || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {batch.expiryDate || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max={batch.availableQuantity !== undefined ? batch.availableQuantity : (batch.originalQuantity || batch.quantity || 0)}
                            value={batch.quantity || 0}
                            onChange={(e) => {
                              const newQty = parseFloat(e.target.value) || 0;
                              const maxQty = batch.availableQuantity !== undefined ? batch.availableQuantity : (batch.originalQuantity || batch.quantity || 0);
                              const validQty = Math.min(Math.max(0, newQty), maxQty);
                              
                              setFormData((prev) => {
                                const updatedBatches = [...prev.batches];
                                updatedBatches[index] = {
                                  ...updatedBatches[index],
                                  quantity: validQty,
                                  // Preserve originalQuantity, availableQuantity, alreadyReturned
                                  originalQuantity: batch.originalQuantity,
                                  availableQuantity: batch.availableQuantity,
                                  alreadyReturned: batch.alreadyReturned,
                                };
                                
                                const updated = {
                                  ...prev,
                                  batches: updatedBatches,
                                };
                                
                                // Validate batches sum when quantity changes
                                const receivedQty = parseFloat(prev.receivedQuantity) || 0;
                                if (receivedQty > 0) {
                                  const batchesSum = updatedBatches.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);
                                  if (batchesSum !== receivedQty) {
                                    setFormErrors((prevErrors) => ({
                                      ...prevErrors,
                                      batches: `Sum of batch quantities (${batchesSum}) must equal received quantity (${receivedQty})`,
                                    }));
                                  } else {
                                    setFormErrors((prevErrors) => {
                                      const newErrors = { ...prevErrors };
                                      delete newErrors.batches;
                                      return newErrors;
                                    });
                                  }
                                }
                                
                                return updated;
                              });
                            }}
                            className="w-24 rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 44, paddingLeft: 14, paddingRight: 14 }}
                          />
                          {batch.originalQuantity && (
                            <span className="text-xs text-gray-500">
                              / {batch.availableQuantity !== undefined ? batch.availableQuantity : batch.originalQuantity} 
                              {batch.alreadyReturned > 0 && (
                                <span className="text-gray-400"> (was {batch.originalQuantity})</span>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td colSpan="3" className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">Total:</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {formData.batches.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </>
          )}
        </div>
      </FormPopup>
      <div className="mb-3 flex items-center justify-end gap-2">
</div>
      <Table
        columns={returnColumns}
        data={transactions}
        loading={loading}
        emptyMessage="No return transactions found"
        actions={returnActions}
      />
    </div>
  );
}
